import { forwardRef } from "react";
import { stripMarkdown } from "@/lib/api";

/**
 * Renderable card used to produce a shareable PNG of an assistant response.
 * Lives inside a modal overlay; sized at a fixed 1080px width and laid out
 * with inline styles so html2canvas / html-to-image can rasterize it
 * predictably (external CSS classes are not honored by SVG foreignObject
 * snapshotting on some browsers, so every visual property is inline).
 *
 * Palette (July 2026): matches the Alpha Compute-inspired app theme —
 * full var(--accent) canvas, pure white type, white-translucent for supporting
 * elements. No terracotta, no stone. If you change the app palette, keep
 * these hexes in sync manually.
 */
const ShareCard = forwardRef(function ShareCard({ question, answer }, ref) {
  const text = stripMarkdown(answer || "");

  return (
    <div
      ref={ref}
      style={{
        width: "1080px",
        background: "var(--accent)",
        color: "#ffffff",
        padding: "110px 100px 90px 100px",
        boxSizing: "border-box",
        fontFamily: "'IBM Plex Sans', sans-serif",
      }}
      data-testid="share-card"
    >
      {/* Domain strip — sits at the very top of the card so the source
          is unmissable when the PNG is reshared out of context. */}
      <div
        style={{
          fontFamily: "'JetBrains Mono', monospace",
          fontSize: "20px",
          letterSpacing: "0.22em",
          textTransform: "lowercase",
          color: "#ffffff",
          fontWeight: 600,
          textAlign: "center",
          paddingBottom: "36px",
          borderBottom: "1px solid rgba(255, 255, 255, 0.35)",
          marginBottom: "40px",
        }}
        data-testid="share-card-url"
      >
        www.humanebehavior.com
      </div>

      {/* Wordmark */}
      <div
        style={{
          fontFamily: "'JetBrains Mono', monospace",
          fontSize: "18px",
          letterSpacing: "0.4em",
          textTransform: "uppercase",
          color: "#ffffff",
          fontWeight: 500,
        }}
      >
        Humane Behavior AI
      </div>

      <div
        style={{
          height: "1px",
          background: "rgba(255, 255, 255, 0.35)",
          marginTop: "26px",
          marginBottom: "60px",
        }}
      />

      {/* Question — small, muted white, sans */}
      {question && (
        <div
          style={{
            display: "flex",
            gap: "16px",
            marginBottom: "50px",
            alignItems: "flex-start",
          }}
        >
          <span
            style={{
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: "14px",
              letterSpacing: "0.24em",
              textTransform: "uppercase",
              color: "#ffffff",
              opacity: 0.75,
              flexShrink: 0,
              marginTop: "4px",
            }}
          >
            Q.
          </span>
          <span
            style={{
              fontFamily: "'IBM Plex Sans', sans-serif",
              fontSize: "22px",
              lineHeight: 1.45,
              color: "rgba(255, 255, 255, 0.85)",
              fontWeight: 400,
            }}
          >
            {question}
          </span>
        </div>
      )}

      {/* Decorative opening quote */}
      <div
        style={{
          fontFamily: "'Cormorant Garamond', serif",
          fontSize: "180px",
          lineHeight: 0.4,
          color: "#ffffff",
          opacity: 0.45,
          height: "60px",
          marginBottom: "10px",
          fontStyle: "italic",
          fontWeight: 500,
        }}
      >
        “
      </div>

      {/* Answer body */}
      <div
        style={{
          fontFamily: "'Cormorant Garamond', serif",
          fontSize: "42px",
          lineHeight: 1.5,
          fontWeight: 400,
          color: "#ffffff",
          whiteSpace: "pre-wrap",
          letterSpacing: "0.005em",
        }}
      >
        {text}
      </div>

      {/* Footer */}
      <div
        style={{
          marginTop: "90px",
          paddingTop: "32px",
          borderTop: "1px solid rgba(255, 255, 255, 0.35)",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          fontFamily: "'JetBrains Mono', monospace",
          fontSize: "14px",
          letterSpacing: "0.28em",
          textTransform: "uppercase",
          color: "rgba(255, 255, 255, 0.7)",
        }}
      >
        <span style={{ color: "#ffffff" }}>Humane Behavior AI</span>
        <span>· The answer textbooks lie about ·</span>
      </div>
    </div>
  );
});

export default ShareCard;
